import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { CloudEvent } from 'cloudevents';
import * as luxon from 'luxon';

import { useConfig } from './useConfig';
import { useAuthentication } from './useAuthentication';
import { useApi } from './useApi';

import {
  WebsocketService,
  WebsocketServiceImpl,
} from '../services/websocketService';

export enum ServerDataPopType {
  datapop = 'agero-event-server-datapop',
  error = 'agero-event-server-error',
  callterminated = 'agero-event-server-callterminated',
}

enum ClientDataPopType {
  datapop = 'agero-event-client-datapop',
}

export interface ServerDataPop {
  message: string;
}

export interface ClientDataPop {
  message: string;
  target: string;
}

export interface DataPopMessage {
  type: ServerDataPopType;
  message: string;
  originator: string;
  timestamp: string;
}

export interface DataPopContextProps {
  messages: DataPopMessage[];
  errors: string[];
  dataPop: (dataPop: ClientDataPop) => void;
}

export const DataPopContext = createContext<DataPopContextProps | null>(null);

export const DataPopProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [dataPopProps, setDataPopProps] = useState<DataPopContextProps | null>(
    null,
  );
  const [messages, setMessages] = useState<DataPopMessage[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const config = useConfig();
  const { auth } = useAuthentication();
  const { api } = useApi();
  const ws = useRef<WebSocket | null>(null);
  const dataPopService = useRef<WebsocketService | null>(null);

  useEffect(() => {
    const websocketUrl = `${config.api.websocketURL}/`;
    ws.current = new WebSocket(websocketUrl);
    const wsDataPop = new WebsocketServiceImpl(ws.current, auth);
    const interval = setInterval(() => {
      if (ws.current && ws.current.readyState === WebSocket.OPEN) {
        return;
      }
      try {
        ws.current!.close();
      } finally {
        ws.current = new WebSocket(websocketUrl);
        wsDataPop.setWebsocket(ws.current);
      }
    }, 5000);
    dataPopService.current = wsDataPop;

    dataPopService.current.subscribe(ServerDataPopType.datapop, event => {
      const chat = mapCloudEventToMessage(event);
      setMessages(m => [...m, chat]);
    });
    dataPopService.current.subscribe(ServerDataPopType.error, event => {
      const chat = mapCloudEventToMessage(event);
      setErrors(e => [...e, chat.message]);
    });

    dataPopService.current.subscribe(
      ServerDataPopType.callterminated,
      event => {
        const chat = mapCloudEventToMessage(event);
        setMessages(m => [...m, chat]);
      },
    );

    return () => {
      ws.current?.close();
      clearInterval(interval);
    };
  }, [api, auth, config]);

  // 2nd useEffect needed to prevent websocket / chatService from being re-created when users/messages/errors are updated
  useEffect(() => {
    if (dataPopService.current === null) {
      return;
    }

    const dataPop = (data: ClientDataPop): void => {
      dataPopService.current!.send({
        type: ClientDataPopType.datapop,
        data: data,
      });
    };

    setDataPopProps({
      messages,
      errors,
      dataPop,
    });
    setLoading(false);
  }, [messages, errors]);

  if (loading) {
    return null;
  }

  return (
    <DataPopContext.Provider value={dataPopProps}>
      {children}
    </DataPopContext.Provider>
  );
};

export function useDataPop(): DataPopContextProps {
  const context = useContext(DataPopContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useDataPop outside of DataPopProvider');
}

function mapCloudEventToMessage(cloudEvent: CloudEvent): DataPopMessage {
  return {
    message: (cloudEvent.data as ServerDataPop).message,
    originator: cloudEvent.source,
    type: cloudEvent.type as ServerDataPopType,
    timestamp: cloudEvent.time || luxon.DateTime.now().toISO(),
  };
}
